import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ReferenceLine,
} from "recharts";
import { Box, useTheme } from "@mui/material";
import dayjs from "dayjs";
import sortBy from "lodash/sortBy";
import { capitalize } from "lodash";
import { ExerciseTypeSet, JourneyAnchors, Units } from "../../../constants";

import { unitSetLabelCoach } from "../../../utils/units";
import InfoBox from "./InfoBox";
import ChartTooltip from "./ChartTooltip";
import ChartCard from "./ChartCard";
import { HighlightItemInfo } from "../components/HighlightItem";
import { NoActivityExerciseMessageBox } from "./NoActivityExerciseMessageBox";

interface ExerciseChartInfo {
  date: string;
  weight: {
    min: number;
    max: number;
  };
}

interface ExerciseMetricChart {
  volumes: ExerciseChartInfo[];
  units: Units;
  type: ExerciseTypeSet;
  highlight?: HighlightItemInfo;
}

const WeightChart = (props: ExerciseMetricChart) => {
  const { volumes, units, type, highlight } = props;
  const theme = useTheme();

  const chartData = useMemo(() => {
    return sortBy(
      volumes.map((v) => ({
        date: dayjs(v.date).format("MMM DD"),
        min: -v.weight.min,
        max: v.weight.max,
        originalMin: v.weight.min,
        originalMax: v.weight.max,
        x: dayjs(v.date).toDate(),
      })),
      (d) => d.x,
    );
  }, [volumes]);

  const [chartMin, chartMax] = useMemo(() => {
    const allMins = chartData.map((d) => d.min);
    const allMaxs = chartData.map((d) => d.max);

    const minValue = Math.min(...allMins, 0);
    const maxValue = Math.max(...allMaxs, 0);

    const padding = 0.1 * (maxValue - minValue || 10);
    const adjustedMin = Math.floor((minValue - padding) / 5) * 5;
    const adjustedMax = Math.ceil((maxValue + padding) / 5) * 5;

    return [adjustedMin, adjustedMax];
  }, [chartData]);

  const chartTicks = useMemo(() => {
    if (!chartData.length) return [0];

    const absMax = Math.max(Math.abs(chartMin), Math.abs(chartMax));
    const step = Math.ceil(absMax / 3 / 5) * 5;

    return [-3 * step, -2 * step, -1 * step, 0, step, 2 * step, 3 * step];
  }, [chartMin, chartMax]);

  return (
    <ChartCard
      title={`${capitalize(type)} overview`}
      classes={{ card: JourneyAnchors.METRIC_CHART }}
      highlight={highlight}
      info={`Look at the minimum and maximum values shown in the stacked bars.`}
    >
      <Box display={"flex"} flexDirection={"column"} gap={2}>
        <Box display={"flex"} gap={3}>
          <InfoBox
            value={Math.min(...volumes.map((v) => v.weight.min))}
            unit={unitSetLabelCoach(units, type)}
            color={theme.palette.text.secondary}
            label="Min Weight"
          />
          <InfoBox
            value={Math.max(...volumes.map((v) => v.weight.max))}
            unit={unitSetLabelCoach(units, type)}
            color={theme.palette.primary.main}
            label="Max Weight"
          />
        </Box>
        <Box position="relative" width="100%" height={308}>
          <ResponsiveContainer
            style={{
              fontFamily: "Montserrat",
              fontWeight: "bold",
              fontSize: 12,
            }}
            width="100%"
            height={308}
          >
            <BarChart
              data={chartData}
              margin={{ right: 50, top: 10 }}
              stackOffset="sign"
            >
              <CartesianGrid strokeWidth={1.5} strokeDasharray="3 3" />
              <XAxis
                strokeWidth={1.5}
                dataKey="x"
                minTickGap={15}
                tickMargin={10}
                interval="preserveStart"
                tickFormatter={(value) => dayjs(value).format("MMM D")}
                stroke={theme.palette.text.secondary}
                fill={theme.palette.text.secondary}
              />
              <YAxis
                domain={[chartMin, chartMax]}
                ticks={chartTicks}
                axisLine={false}
                tickLine={false}
                strokeWidth={1.5}
                yAxisId={0}
                stroke={theme.palette.text.secondary}
                fill={theme.palette.text.secondary}
                tickFormatter={(v) =>
                  v >= 1000 ? `${Math.abs(v) / 1000}k` : Math.abs(v).toString()
                }
              />

              <ReferenceLine
                y={0}
                stroke={theme.palette.divider}
                strokeWidth={2}
              />

              <RechartsTooltip
                animationDuration={300}
                animationEasing="linear"
                cursor={false}
                content={ChartTooltip({
                  maxColor: theme.palette.primary.main,
                  minColor: theme.palette.text.secondary,
                  unitLabel: unitSetLabelCoach(units, type).toLowerCase(),
                })}
              />

              <Bar
                dataKey="min"
                stackId="stack"
                fill={theme.palette.text.secondary}
                width={26}
                radius={[0, 0, 4, 4]}
                isAnimationActive={true}
                animationDuration={500}
                animationEasing="ease-out"
              />
              <Bar
                dataKey="max"
                stackId="stack"
                width={26}
                fill={theme.palette.primary.main}
                radius={[4, 4, 0, 0]}
                isAnimationActive={true}
                animationDuration={500}
                animationEasing="ease-out"
              />
            </BarChart>
          </ResponsiveContainer>
          {chartData.length === 0 && <NoActivityExerciseMessageBox />}
        </Box>
      </Box>
    </ChartCard>
  );
};

export default WeightChart;
